import { Define } from '../../../core/decorators';
import { TAG_INPUT_TEXT } from '../../../core/utils/constants/tags';
import { HTMLBnumInput } from '../input/bnum-input';
import { ATTRIBUTE_TYPE } from '../input/bnum-input.internal/constants';
import { BnumInputCreateOptions } from '../input/bnum-input.internal/types';

const TYPE_TEXT = 'text';

type BnumInputTextCreateOption = Omit<
	BnumInputCreateOptions,
	'text' | 'min' | 'max' | 'step'
>;

/**
 * Input texte.
 *
 * @category Input
 *
 * @structure Sans rien
 * <bnum-input-text></bnum-input-text>
 *
 * @structure Avec une légende
 * <bnum-input-text>Label du champ</bnum-input-text>
 *
 * @structure Avec une légende et un indice
 * <bnum-input-text>
 * Label du champ
 * <span slot="hint">Indice d'utilisation</span>
 * </bnum-input-text>
 *
 * @structure Avec un bouton
 * <bnum-input-text button="true" button-icon="add">Label du champ
 *   <span slot="button">Ajouter</span>
 * </bnum-input-text>
 *
 * @structure En erreur
 * <bnum-input-text pattern="^[a-zA-Z0-9]+$" data-value="@@@@@">Label du champ
 * </bnum-input-text>
 *
 * @structure Avec un état de succès
 * <bnum-input-text state="success">Label du champ
 *   <span slot="success">Le champ est valide !</span>
 * </bnum-input-text>
 *
 * @structure Avec une icône
 * <bnum-input-text icon="search">Label du champ</bnum-input-text>
 *
 * @structure Avec un bouton avec icône seulement
 * <bnum-input-text placeholder="LA LA !" button-icon="add">Label du champ
 * </bnum-input-text>
 *
 * @structure Désactivé
 * <bnum-input-text disabled>
 *   Label du champ
 * </bnum-input-text>
 *
 * @structure Complet
 * <bnum-input-text
 *   data-value="Valeur initiale"
 *   placeholder="Texte indicatif"
 *   type="text"
 *   state="error"
 *   icon="search"
 *   button="primary"
 *   button-icon="send"
 * >
 *   Label du champ
 *   <span slot="hint">Indice d'utilisation</span>
 *   <span slot="success">Le champ est valide !</span>
 *   <span slot="error">Le champ est invalide !</span>
 *   <span slot="button">Envoyer</span>
 * </bnum-input-text>
 *
 * @attr {string} (optional) (default: 'text') type - Type de l'input (text, password, email, etc.) Ne pas modifier, toujours 'text' pour ce composant.
 *
 */
@Define({ tag: TAG_INPUT_TEXT })
export class HTMLBnumInputText extends HTMLBnumInput {
	constructor() {
		super();
	}

	protected _p_preload(): void {
		super._p_preload();
		this.setAttribute(ATTRIBUTE_TYPE, TYPE_TEXT);
	}

	/**
	 *@inheritdoc
	 */
	protected _p_buildDOM(): void {
		super._p_buildDOM();
	}

	/**
	 *@inheritdoc
	 */
	static _p_observedAttributes(): string[] {
		return super._p_observedAttributes().filter(x => x !== ATTRIBUTE_TYPE);
	}

	/**
	 * Crée une instance du composant avec les options fournies.
	 * @param label Texte du label principal.
	 * @param options Options d'initialisation (attributs et slots).
	 * @returns  Instance du composant.
	 */
	static Create(label: string, options: BnumInputTextCreateOption = {}) {
		const finalOptions = {
			type: TYPE_TEXT,
			...options,
		};
		return super.Create(label, finalOptions) as HTMLBnumInputText;
	}
}
